import React, { useEffect, useState } from 'react';
import { Alert, Col, Form as ReactForm, Row } from 'react-bootstrap';
import { mfaApi, MfaRegistrationStatus, MfaRegistrationStatusStatusEnum } from '../../apis';
import './MfaSettings.scss';
import { ErrorMessage, Form, Formik, FormikHelpers, FormikProps, FormikValues } from 'formik';
import * as Yup from 'yup';
import LoadingOverlay from '../../feedback/LoadingOverlay';
import { Button } from '@salvapp/ui-components';

export interface TwoFactorCodeFormValues {
  otpCode: string;
}

const validationSchema = Yup.object().shape({
  otpCode: Yup.string().required('Required').matches(new RegExp(/^\d+$/), 'Digits only'),
});

export const MfaSettings = ({
  onRegistrationStatusUpdate,
}: {
  onRegistrationStatusUpdate: (status: MfaRegistrationStatus) => void;
}): React.JSX.Element => {
  const [registrationStatus, setRegistrationStatus] = useState<null | MfaRegistrationStatus>(null);

  const [qrCodeString, setQrCodeString] = useState<null | string>(null);

  const [loading, setLoading] = useState<boolean>(true);

  const initialValues = {
    otpCode: '',
  };

  useEffect(() => {
    setLoading(true);
    mfaApi.registrationStatus().then((status) => {
      if (status.status === MfaRegistrationStatusStatusEnum.NONE) {
        mfaApi.getRegistrationCode().then((mfaRegistration) => {
          setQrCodeString(mfaRegistration.qrCodeString);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
      onRegistrationStatusUpdate(status);
      setRegistrationStatus(status);
    });
  }, [onRegistrationStatusUpdate]);

  const handleStatus = (status: MfaRegistrationStatus) => {
    setRegistrationStatus(status);
    onRegistrationStatusUpdate(status);
    if (status.status !== MfaRegistrationStatusStatusEnum.COMPLETE) {
      mfaApi.getRegistrationCode().then((mfaRegistration) => {
        setQrCodeString(mfaRegistration.qrCodeString);
      });
    }
  };

  const deactivate = async (values: FormikValues, { setSubmitting, setStatus }: FormikHelpers<FormikValues>) => {
    setSubmitting(true);
    mfaApi.remove().then((response) => {
      if (!response.success) {
        setStatus('Could not remove multi-factor authentication');
        setSubmitting(false);
      } else {
        mfaApi.registrationStatus().then((status) => {
          setStatus(undefined);
          handleStatus(status);
          setSubmitting(false);
        });
      }
    });
  };

  const submit = async (
    values: TwoFactorCodeFormValues,
    { setSubmitting, setStatus }: FormikHelpers<TwoFactorCodeFormValues>
  ) => {
    setSubmitting(true);
    mfaApi
      .register({
        mfaCode: { mfaCode: values.otpCode },
      })
      .then((result) => {
        if (result.success) {
          mfaApi.registrationStatus().then((status) => {
            setStatus(undefined);
            handleStatus(status);
            setSubmitting(false);
          });
        } else {
          setStatus(result.message);
          setSubmitting(false);
        }
      });
  };

  if (registrationStatus?.status === MfaRegistrationStatusStatusEnum.FEATURE_NOT_ENABLED) {
    return <div>Feature disabled</div>;
  }

  return (
    <LoadingOverlay loading={loading}>
      <div>
        {qrCodeString && registrationStatus?.status !== MfaRegistrationStatusStatusEnum.COMPLETE && (
          <div>
            <div className='small'>
              Two-Factor Authentication (2FA) helps to increase your account
              {"'"}s security. To enable two-factor authentication, scan the following QR code with your 2FA application
              and enter the 2FA code.
            </div>

            <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema}>
              {({
                values,
                isSubmitting,
                touched,
                errors,
                handleChange,
                status,
              }: FormikProps<TwoFactorCodeFormValues>) => {
                return (
                  <Form>
                    <ReactForm.Group as={Row} className='form-group'>
                      <Col className='qr-container'>
                        <img height={250} alt='qr code' src={qrCodeString} />
                      </Col>
                    </ReactForm.Group>

                    <ReactForm.Group as={Row} className='form-group'>
                      <Col md={3}>
                        <ReactForm.Label column className='text-end'>
                          Code
                        </ReactForm.Label>
                      </Col>
                      <Col md={6}>
                        <ReactForm.Control
                          type='text'
                          name='otpCode'
                          placeholder='code'
                          value={values.otpCode}
                          onChange={handleChange}
                          isInvalid={touched.otpCode && !!errors.otpCode}
                        />
                        <ErrorMessage
                          name='otpCode'
                          render={(message) => (
                            <ReactForm.Control.Feedback type='invalid'>{message}</ReactForm.Control.Feedback>
                          )}
                        />
                      </Col>
                    </ReactForm.Group>

                    <ReactForm.Group as={Row} className='form-group'>
                      <Col md={{ offset: 3, span: 6 }} className='text-center'>
                        <Button type='submit' disabled={isSubmitting} variant='primary'>
                          Activate
                        </Button>
                      </Col>
                    </ReactForm.Group>

                    <ReactForm.Group as={Row} className='form-group'>
                      <Col md={{ offset: 4, span: 4 }}>{status && <Alert variant='danger'>{status}</Alert>}</Col>
                    </ReactForm.Group>
                  </Form>
                );
              }}
            </Formik>
          </div>
        )}
        {registrationStatus?.status === MfaRegistrationStatusStatusEnum.COMPLETE && (
          <Formik initialValues={{}} onSubmit={deactivate} enableReinitialize={true}>
            {({ isSubmitting, status }) => {
              return (
                <Form className='disable-mfa-form'>
                  <ReactForm.Group as={Row} className='form-group'>
                    <Col md={6}>
                      <Button type='submit' disabled={isSubmitting} variant='primary'>
                        Disable Two-factor authentication
                      </Button>
                    </Col>
                  </ReactForm.Group>

                  {status && (
                    <ReactForm.Group as={Row} className='form-group'>
                      <Col md={6}>
                        <Alert variant='danger'>{status}</Alert>
                      </Col>
                    </ReactForm.Group>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
      </div>
    </LoadingOverlay>
  );
};

export default MfaSettings;
